import { forwardRef } from "react";
import { Check, Minus } from "lucide-react";
import styles from "./Checkbox.module.css";

export const Checkbox = forwardRef(
  (
    {
      checked = false,
      onChange,
      name,
      id,
      label,
      description,
      indeterminate = false,
      variant = "standard",
      disabled = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const handleChange = (e) => {
      if (disabled) return;
      if (onChange) {
        onChange({ target: { name, checked: e.target.checked } });
      }
    };

    const wrapperClass = [
      styles.checkboxWrapper,
      variant === "card" ? styles.cardVariant : "",
      checked ? styles.isChecked : "",
      disabled ? styles.disabled : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <label htmlFor={id || name} className={wrapperClass}>
        <input
          ref={ref}
          type="checkbox"
          id={id || name}
          name={name}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className={styles.hiddenInput}
          {...props}
        />

        <span
          className={`${styles.customBox} ${
            checked || indeterminate ? styles.checked : ""
          }`}
          aria-hidden="true"
        >
          {indeterminate ? (
            <Minus size={12} strokeWidth={3} />
          ) : checked ? (
            <Check size={12} strokeWidth={3} />
          ) : null}
        </span>

        {(label || description) && (
          <div className={styles.textGroup}>
            {label && <span className={styles.label}>{label}</span>}
            {description && (
              <span className={styles.description}>{description}</span>
            )}
          </div>
        )}
      </label>
    );
  }
);

Checkbox.displayName = "Checkbox";

export const CheckboxGroup = ({
  options = [],
  value = [],
  onChange,
  name,
  direction = "vertical",
  variant = "standard",
  disabled = false,
  className = "",
}) => {
  const currentValues = Array.isArray(value) ? value : [];

  const handleOptionChange = (optionVal, isChecked) => {
    let nextValues;
    if (isChecked) {
      nextValues = [...currentValues, optionVal];
    } else {
      nextValues = currentValues.filter((v) => v !== optionVal);
    }
    if (onChange) {
      onChange({ target: { name, value: nextValues } });
    }
  };

  return (
    <div
      className={`${styles.groupContainer} ${
        direction === "horizontal" ? styles.horizontal : styles.vertical
      } ${className}`.trim()}
    >
      {options.map((opt) => {
        const optVal = opt.value !== undefined ? opt.value : opt;
        const optLabel = opt.label || String(opt);
        const isChecked = currentValues.includes(optVal);

        return (
          <Checkbox
            key={optVal}
            name={`${name}_${optVal}`}
            label={optLabel}
            description={opt.description}
            checked={isChecked}
            onChange={(e) => handleOptionChange(optVal, e.target.checked)}
            variant={variant}
            disabled={disabled || opt.disabled}
          />
        );
      })}
    </div>
  );
};

export default Checkbox;
